import { create } from "zustand"
import { identifyUser, resetUser } from "@/lib/posthog"
import { setSentryUser } from "@/lib/sentry"

export type Role = "SUPER_ADMIN" | "CLIENT_ADMIN" | "PROCTOR" | "CANDIDATE"

export interface AuthUser {
  id: string
  email: string
  fullName?: string
  subdomain?: string
  clientAdminId?: string
  avatarUrl?: string | null
  jobTitle?: string | null
  companyName?: string | null
  // Module access flags — Super Admin controlled. Drive the sidebar lock
  // icons + route guards. Optional so old persisted sessions don't crash
  // before they refresh via /api/profile; the helper functions in
  // lib/module-access.ts treat undefined as "false" (locked).
  moduleAssessments?: boolean
  moduleMultirater?: boolean
  moduleQrQuiz?: boolean
  moduleProctoring?: boolean
  moduleAiAssistant?: boolean
  modulePsychometrics?: boolean
}

interface AuthState {
  role: Role | null
  user: AuthUser | null
  // Reactive "am I in an impersonation session" flag. Set from GET /auth/me via
  // hydrateFromMe (the source of truth is the httpOnly session cookie). The
  // banner's client name/email are read from the non-httpOnly `impersonation`
  // cookie in the client layout, not held here.
  isImpersonating: boolean
  // S6 5b/A5: false until the initial GET /api/auth/me bootstrap resolves (or is
  // skipped because no session is hinted). Gates the boot loader in ProtectedRoute.
  bootstrapped: boolean
  // S6 5b/B1: identity carries NO token — the JWT lives only in the httpOnly
  // auth_token cookie. setAuth is used by the login/verify sites (from the login
  // response `user`); hydrateFromMe is used by the /auth/me bootstrap.
  setAuth: (args: { role: Role; user: AuthUser }) => void
  hydrateFromMe: (data: { role: Role; user: AuthUser; isImpersonation?: boolean }) => void
  setBootstrapped: (v: boolean) => void
  clear: () => void
}

/* S6 5b/B1: the JWT is NEVER stored in JS — it lives only in the httpOnly
 * auth_token cookie. We still seed {role, user} synchronously from localStorage
 * at module load so <ProtectedRoute> has identity on the very first render (no
 * flicker) for returning users; the async /auth/me bootstrap refreshes it and
 * covers the cookie-only / cleared-storage case. role/user are not credentials
 * (they're already visible throughout the UI), so persisting them is safe — the
 * sensitive token is out of JS reach entirely.
 */

const STORAGE_KEY = "yoma.auth"

interface PersistedAuth {
  state?: {
    role?: Role | null
    user?: AuthUser | null
  }
}

interface InitialAuth {
  role: Role | null
  user: AuthUser | null
}

function readStoredAuth(): InitialAuth {
  if (typeof window === "undefined") return { role: null, user: null }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return { role: null, user: null }
    const parsed = JSON.parse(raw) as PersistedAuth
    const s = parsed.state ?? {}
    return {
      role: (s.role as Role | null | undefined) ?? null,
      user: (s.user as AuthUser | null | undefined) ?? null,
    }
  } catch {
    return { role: null, user: null }
  }
}

function writeStoredAuth(role: Role, user: AuthUser) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ state: { role, user }, version: 1 }),
    )
  } catch {
    // localStorage may be disabled in some browsing modes — non-fatal.
  }
}

function clearStoredAuth() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // noop
  }
}

// Push the current identity to PostHog + Sentry (both no-op when their
// keys/DSN aren't configured, so this is safe in every environment).
function syncIdentity(role: Role, user: AuthUser) {
  identifyUser({
    id: user.id,
    email: user.email,
    fullName: user.fullName ?? user.email,
    role,
  })
  setSentryUser({ id: user.id, email: user.email, role })
}

function clearIdentity() {
  resetUser()
  setSentryUser(null)
}

const initial = readStoredAuth()

export const useAuthStore = create<AuthState>((set) => ({
  role: initial.role,
  user: initial.user,
  isImpersonating: false,
  bootstrapped: false,
  // Login/verify sites call this with the login response's `user` (its shape
  // matches /auth/me, verified in phase 5a). No token: the cookie carries auth.
  setAuth: ({ role, user }) => {
    writeStoredAuth(role, user)
    set({ role, user, bootstrapped: true })
    syncIdentity(role, user)
  },
  // Refresh identity from the cookie session (GET /auth/me). Persists {role,user}
  // so the next load's synchronous seed stays warm.
  hydrateFromMe: ({ role, user, isImpersonation }) => {
    writeStoredAuth(role, user)
    set({ role, user, isImpersonating: !!isImpersonation, bootstrapped: true })
    syncIdentity(role, user)
  },
  setBootstrapped: (v) => set({ bootstrapped: v }),
  clear: () => {
    clearStoredAuth()
    set({
      role: null,
      user: null,
      isImpersonating: false,
      // Cleared = we definitively know the user is logged out; stop gating so
      // ProtectedRoute redirects instead of showing the boot loader.
      bootstrapped: true,
    })
    clearIdentity()
  },
}))
